/**
 * Organization Managed Billing Card
 *
 * Whether JotPsych's billing team works this organization's claims. Managed
 * organizations make up the Morning Worklist's default view and the agent's
 * managed list. The setting is independent of the JotBill take: an
 * organization can be managed with no participation fee.
 *
 * Marking an organization managed without a signed managed agreement (MSA)
 * warns and asks for a reason, which is saved with the change.
 */

import { useState } from 'react';

import { type AutosaveState, AutosaveStatus } from '@components/AutosaveStatus';
import { SupportAgent as SupportAgentIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

type BillingService = 'managed' | 'self_serve';

export interface OrganizationManagedBillingCardProps {
  /** Undefined until the setting has loaded */
  managed: boolean | undefined;
  updatedAt?: string | null;
  updatedBy?: string | null;
  isLoading: boolean;
  /** The setting could not be loaded */
  loadFailed?: boolean;
  onRetry?: () => void;
  /** True when the organization signed a managed agreement package */
  signedManagedAgreement?: boolean;
  saveStatus: AutosaveState;
  /**
   * `reason` is given when marking managed without a signed agreement.
   * Resolves to whether the change saved; the reason prompt stays open with
   * the text until it does.
   */
  onChange: (managed: boolean, reason?: string) => Promise<boolean> | void;
}

const REASON_MAX_LENGTH = 1000;

const DESCRIPTIONS: Record<BillingService, string> = {
  managed:
    "JotPsych's billing team works this organization's claims. It is on the Morning Worklist's default view.",
  self_serve:
    'The practice bills for itself. It is on the Morning Worklist only under All organizations.',
};

export function OrganizationManagedBillingCard({
  managed,
  updatedAt,
  updatedBy,
  isLoading,
  loadFailed = false,
  onRetry,
  signedManagedAgreement,
  saveStatus,
  onChange,
}: OrganizationManagedBillingCardProps) {
  const theme = useTheme();
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const trimmedReason = reason.trim();

  const choose = (next: BillingService) => {
    if (next === 'managed' && signedManagedAgreement !== true) {
      setReason('');
      setSaveFailed(false);
      setReasonOpen(true);
      return;
    }
    void onChange(next === 'managed');
  };

  const confirmManaged = async () => {
    if (!trimmedReason || submitting) return;
    setSubmitting(true);
    setSaveFailed(false);
    try {
      const saved = await onChange(true, trimmedReason);
      if (saved === false) {
        setSaveFailed(true);
      } else {
        setReasonOpen(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const closeReason = () => {
    if (!submitting) setReasonOpen(false);
  };
  const value: BillingService | null =
    managed === undefined ? null : managed ? 'managed' : 'self_serve';

  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
        height: '100%',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SupportAgentIcon color={managed ? 'success' : 'disabled'} sx={{ mr: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Billing service
            </Typography>
          </Box>
          <AutosaveStatus status={saveStatus} isDirty={false} />
        </Box>

        {loadFailed ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="error">
              Could not load the billing service.
            </Typography>
            <Button size="small" onClick={onRetry}>
              Retry
            </Button>
          </Box>
        ) : isLoading || value === null ? (
          <CircularProgress size={20} />
        ) : (
          <>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={value}
              disabled={saveStatus === 'saving'}
              // MUI passes null when the active segment is re-clicked; keep one on.
              onChange={(_, next: BillingService | null) => {
                if (next && next !== value) choose(next);
              }}
              aria-label="Billing service"
            >
              <ToggleButton value="managed">Managed</ToggleButton>
              <ToggleButton value="self_serve">Self-serve</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {DESCRIPTIONS[value]}
            </Typography>
            {updatedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Last updated {new Date(updatedAt).toLocaleString()}
                {updatedBy ? ` by ${updatedBy}` : ''}
              </Typography>
            )}
          </>
        )}
      </CardContent>
      <Dialog open={reasonOpen} onClose={closeReason} maxWidth="sm" fullWidth>
        <DialogTitle>Mark managed without a signed agreement?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This organization has no managed agreement (MSA) signed through the Intake Hub.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You can still mark it managed. Say why; the reason is saved with the change.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            required
            label="Reason"
            placeholder="MSA signed on paper, see Drive"
            value={reason}
            onChange={event => setReason(event.target.value)}
            slotProps={{ htmlInput: { maxLength: REASON_MAX_LENGTH } }}
          />
          {saveFailed && (
            <Alert severity="error" sx={{ mt: 2 }}>
              The change was not saved. Your reason is kept; try again.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReason} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!trimmedReason || submitting}
            onClick={() => void confirmManaged()}
          >
            Mark managed
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
